/**
 * Minimal markdown renderer for specialist artifact summaries — headings,
 * bold, lists, blockquotes, and pipe tables. No dependency, no raw HTML
 * injection (everything renders through React text nodes).
 */

import React from 'react';

function renderInline(text: string, key: number): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let rest = text;
  let i = 0;
  while (rest.length) {
    const bold = rest.match(/\*\*(.+?)\*\*/);
    const code = rest.match(/`([^`]+)`/);
    const first =
      bold && (!code || bold.index! <= code.index!)
        ? { match: bold, type: 'bold' as const }
        : code
          ? { match: code, type: 'code' as const }
          : null;
    if (!first) {
      parts.push(rest);
      break;
    }
    const { match, type } = first;
    if (match.index! > 0) parts.push(rest.slice(0, match.index));
    if (type === 'bold') {
      parts.push(<strong key={`${key}-${i}`}>{match[1]}</strong>);
    } else {
      parts.push(
        <code
          key={`${key}-${i}`}
          style={{
            background: 'var(--p-color-bg-surface-secondary)',
            padding: '1px 4px',
            borderRadius: 4,
            fontSize: '0.9em',
          }}
        >
          {match[1]}
        </code>
      );
    }
    rest = rest.slice(match.index! + match[0].length);
    i += 1;
  }
  return parts;
}

export function SimpleMarkdown({ text }: { text: string }) {
  const lines = text.split('\n');
  const blocks: React.ReactNode[] = [];
  let listItems: string[] = [];
  let tableRows: string[][] = [];

  const flushList = (key: string) => {
    if (listItems.length) {
      blocks.push(
        <ul key={key} style={{ margin: '4px 0 12px 20px' }}>
          {listItems.map((item, i) => (
            <li key={i} style={{ marginBottom: 4 }}>
              {renderInline(item, i)}
            </li>
          ))}
        </ul>
      );
      listItems = [];
    }
  };

  const flushTable = (key: string) => {
    if (tableRows.length) {
      const [head, ...body] = tableRows;
      blocks.push(
        <div key={key} style={{ overflowX: 'auto', margin: '8px 0 12px' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                {head.map((cell, i) => (
                  <th
                    key={i}
                    style={{
                      textAlign: 'left',
                      borderBottom: '1px solid var(--p-color-border)',
                      padding: '4px 8px',
                    }}
                  >
                    {renderInline(cell, i)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {body.map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell, ci) => (
                    <td
                      key={ci}
                      style={{
                        borderBottom:
                          '1px solid var(--p-color-border-secondary)',
                        padding: '4px 8px',
                      }}
                    >
                      {renderInline(cell, ci)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      tableRows = [];
    }
  };

  lines.forEach((line, index) => {
    const trimmed = line.trim();
    const key = `b${index}`;
    if (trimmed.startsWith('|')) {
      flushList(`${key}-l`);
      const cells = trimmed
        .split('|')
        .slice(1, -1)
        .map((c) => c.trim());
      if (cells.every((c) => /^[-: ]*$/.test(c))) return; // separator row
      tableRows.push(cells);
      return;
    }
    flushTable(`${key}-t`);
    if (trimmed.startsWith('- ')) {
      listItems.push(trimmed.slice(2));
      return;
    }
    flushList(`${key}-l`);
    if (!trimmed) return;
    if (trimmed.startsWith('### ')) {
      blocks.push(
        <h4 key={key} style={{ margin: '14px 0 6px', fontWeight: 650 }}>
          {renderInline(trimmed.slice(4), index)}
        </h4>
      );
    } else if (trimmed.startsWith('## ')) {
      blocks.push(
        <h3 key={key} style={{ margin: '16px 0 6px', fontWeight: 700 }}>
          {renderInline(trimmed.slice(3), index)}
        </h3>
      );
    } else if (trimmed.startsWith('> ')) {
      blocks.push(
        <blockquote
          key={key}
          style={{
            borderLeft: '3px solid var(--p-color-border)',
            margin: '8px 0',
            padding: '4px 12px',
            color: 'var(--p-color-text-secondary)',
          }}
        >
          {renderInline(trimmed.slice(2), index)}
        </blockquote>
      );
    } else {
      blocks.push(
        <p key={key} style={{ margin: '6px 0' }}>
          {renderInline(trimmed, index)}
        </p>
      );
    }
  });
  flushList('tail-l');
  flushTable('tail-t');
  return <div>{blocks}</div>;
}
